import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { ArrowLeft, Plus, Trash2, Save, Loader2, HelpCircle } from "lucide-react";
import { Badge } from "../../ui/badge";
import { getCompanyName } from "@/lib/textron-data-processor";

interface ManagementUseOfExpertTailoringQuestionsProps {
  onBack?: () => void;
}

interface ExpertColumn {
  id: string;
  label: string;
}

interface ExpertData {
  [expertId: string]: {
    name: string;
    organization: string;
    managementEstimate: string;
  };
}

export default function ManagementUseOfExpertTailoringQuestions({
  onBack,
}: ManagementUseOfExpertTailoringQuestionsProps) {
  const [companyName, setCompanyName] = useState<string>("");
  const [expertColumns, setExpertColumns] = useState<ExpertColumn[]>([
    { id: "expert-1", label: "Expert 1" },
  ]);
  const [expertData, setExpertData] = useState<ExpertData>({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showGuidance, setShowGuidance] = useState(false);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load saved data
  useEffect(() => {
    const loadData = async () => {
      if (!companyName) return;

      try {
        // Load directly from Execution_TailoringQuestions_Responses.json
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson('Execution_TailoringQuestions_Responses.json');

          if (result?.success && result?.data) {
            // Get data for "Management Use of Expert" area
            const areaData = result.data["Management Use of Expert"];
            
            if (areaData) {
              const savedData = areaData["mue-expert-details"];
              
              if (savedData?.table) {
                // Reconstruct columns and data from saved table
                const savedTable = savedData.table;
                
                // Extract expert columns from table structure
                // Table format: { columns: string[], rows: Array<{ [key: string]: string }> }
                if (savedTable.columns && savedTable.columns.length > 1) {
                  // First column is "Particulars", rest are expert columns
                  const expertCols = savedTable.columns.slice(1).map((col: string, index: number) => ({
                    id: `expert-${index + 1}`,
                    label: col,
                  }));
                  
                  if (expertCols.length > 0) {
                    setExpertColumns(expertCols);
                  }

                  // Reconstruct expert data from rows
                  if (savedTable.rows && savedTable.rows.length >= 3) {
                    const newExpertData: ExpertData = {};
                    
                    expertCols.forEach((col, colIndex) => {
                      const expertId = col.id;
                      newExpertData[expertId] = {
                        name: savedTable.rows[0]?.[col.label] || "",
                        organization: savedTable.rows[1]?.[col.label] || "",
                        managementEstimate: savedTable.rows[2]?.[col.label] || "",
                      };
                    });
                    
                    setExpertData(newExpertData);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to load saved data:", error);
      }
    };

    loadData();
  }, [companyName]);

  const addExpertColumn = () => {
    const newExpertNumber = expertColumns.length + 1;
    const newColumn: ExpertColumn = {
      id: `expert-${newExpertNumber}`,
      label: `Expert ${newExpertNumber}`,
    };
    setExpertColumns([...expertColumns, newColumn]);
    
    // Initialize empty data for new expert
    setExpertData((prev) => ({
      ...prev,
      [newColumn.id]: {
        name: "",
        organization: "",
        managementEstimate: "",
      },
    }));
  };

  const removeExpertColumn = (expertId: string) => {
    if (expertColumns.length <= 1) {
      alert("At least one expert column is required.");
      return;
    }

    setExpertColumns(expertColumns.filter((col) => col.id !== expertId));
    
    // Remove data for deleted expert
    setExpertData((prev) => {
      const newData = { ...prev };
      delete newData[expertId];
      return newData;
    });
  };

  const updateExpertData = (
    expertId: string,
    field: "name" | "organization" | "managementEstimate",
    value: string
  ) => {
    setExpertData((prev) => ({
      ...prev,
      [expertId]: {
        ...(prev[expertId] || { name: "", organization: "", managementEstimate: "" }),
        [field]: value,
      },
    }));
  };

  const handleSave = useCallback(async () => {
    if (!companyName) {
      setSaveError("Company name not available");
      setSaveStatus('error');
      return;
    }

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      // Build table structure for saving
      const tableColumns = ["Particulars", ...expertColumns.map((col) => col.label)];
      const tableRows = [
        {
          Particulars: "Name of the expert",
          ...expertColumns.reduce((acc, col) => {
            acc[col.label] = expertData[col.id]?.name || "";
            return acc;
          }, {} as Record<string, string>),
        },
        {
          Particulars: "Organization of the expert",
          ...expertColumns.reduce((acc, col) => {
            acc[col.label] = expertData[col.id]?.organization || "";
            return acc;
          }, {} as Record<string, string>),
        },
        {
          Particulars: "Management estimate for which the information is provided by management expert",
          ...expertColumns.reduce((acc, col) => {
            acc[col.label] = expertData[col.id]?.managementEstimate || "";
            return acc;
          }, {} as Record<string, string>),
        },
      ];

      const responses = {
        "mue-expert-details": {
          questionId: "mue-expert-details",
          selectedOption: "",
          textResponse: null,
          documents: [],
          table: {
            columns: tableColumns,
            rows: tableRows,
          },
          lastUpdated: new Date().toISOString(),
          companyName,
        },
      };

      if (window.tailoringQuestionsAPI?.saveResponses) {
        const result = await window.tailoringQuestionsAPI.saveResponses({
          companyName,
          areaName: "Management Use of Expert",
          responses,
        });

        if (result.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          throw new Error(result.error || "Failed to save");
        }
      } else {
        throw new Error("Tailoring Questions API not available");
      }
    } catch (error) {
      console.error("Failed to save responses:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [companyName, expertColumns, expertData]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">
            Management Use of Expert - Tailoring Questions
          </h2>
          <p className="text-gray-400">
            Customize the audit program for Management Use of Expert
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Tailoring Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Question */}
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <Label className="text-white text-base font-medium flex-1">
                Document the details of the management's experts which have prepared the information to be used as audit evidence.
              </Label>
              <button
                type="button"
                onClick={() => setShowGuidance(!showGuidance)}
                className="flex-shrink-0 p-1 text-white/60 hover:text-white hover:bg-white/10 rounded transition-colors"
                aria-label="Toggle guidance"
              >
                <HelpCircle className="h-5 w-5" />
              </button>
            </div>

            {/* SA 500 Guidance */}
            {showGuidance && (
              <div className="ml-8 mt-3 p-4 bg-white/5 border border-white/10 rounded-md">
                <div className="text-sm font-semibold text-white/90 mb-3">
                  Standard on Auditing 500 – Audit Evidence paragraph 8
                </div>
                <div className="text-xs text-white/80 mb-3">
                  When the information to be used as audit evidence has been prepared using management's expert, the auditor shall, to the extent necessary, having regard to the significance of that expert's work for the auditor's purposes:
                </div>
                <ul className="list-none space-y-2 text-xs text-white/80">
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 font-semibold">a)</span>
                    <span>Evaluate the competence, capabilities and objectivity of that expert;</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 font-semibold">b)</span>
                    <span>Obtain an understanding of the work of that expert; and</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 font-semibold">c)</span>
                    <span>Evaluate the appropriateness of that expert's work as audit evidence for the relevant assertion.</span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Expert Details Table */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-white">Expert Details</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addExpertColumn}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Expert
              </Button>
            </div>

            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-white/10 bg-black/20">
                    <th className="border-r border-white/10 p-3 text-left text-sm font-medium text-white">
                      Particulars
                    </th>
                    {expertColumns.map((column) => (
                      <th
                        key={column.id}
                        className="border-r border-white/10 p-3 text-center text-sm font-medium text-white relative"
                      >
                        {column.label}
                        {expertColumns.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeExpertColumn(column.id)}
                            className="absolute right-1 top-1 h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-500/10"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Name of the expert row */}
                  <tr className="border-b border-white/10">
                    <td className="border-r border-white/10 p-3 text-sm font-medium text-white bg-black/20">
                      Name of the expert
                    </td>
                    {expertColumns.map((column) => (
                      <td key={column.id} className="border-r border-white/10 p-3">
                        <Input
                          value={expertData[column.id]?.name || ""}
                          onChange={(e) =>
                            updateExpertData(column.id, "name", e.target.value)
                          }
                          className="w-full border-white/10 bg-black/40 text-white placeholder:text-white/50"
                          placeholder="Enter expert name"
                        />
                      </td>
                    ))}
                  </tr>

                  {/* Organization of the expert row */}
                  <tr className="border-b border-white/10">
                    <td className="border-r border-white/10 p-3 text-sm font-medium text-white bg-black/20">
                      Organization of the expert
                    </td>
                    {expertColumns.map((column) => (
                      <td key={column.id} className="border-r border-white/10 p-3">
                        <Input
                          value={expertData[column.id]?.organization || ""}
                          onChange={(e) =>
                            updateExpertData(column.id, "organization", e.target.value)
                          }
                          className="w-full border-white/10 bg-black/40 text-white placeholder:text-white/50"
                          placeholder="Enter organization"
                        />
                      </td>
                    ))}
                  </tr>

                  {/* Management estimate row */}
                  <tr>
                    <td className="border-r border-white/10 p-3 text-sm font-medium text-white bg-black/20">
                      Management estimate for which the information is provided by management expert
                    </td>
                    {expertColumns.map((column) => (
                      <td key={column.id} className="border-r border-white/10 p-3">
                        <Input
                          value={expertData[column.id]?.managementEstimate || ""}
                          onChange={(e) =>
                            updateExpertData(column.id, "managementEstimate", e.target.value)
                          }
                          className="w-full border-white/10 bg-black/40 text-white placeholder:text-white/50"
                          placeholder="Enter management estimate"
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end items-center gap-4 pt-4">
            {saveStatus === 'saved' && (
              <Badge variant="outline" className="border-green-600 text-green-600">
                Saved successfully
              </Badge>
            )}
            {saveStatus === 'error' && saveError && (
              <Badge variant="destructive" className="text-red-600">
                Error: {saveError}
              </Badge>
            )}
            <Button
              onClick={handleSave}
              className="flex items-center gap-2"
              disabled={saving || !companyName}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
